import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { calcDoc, fmt, genNum, today } from '../../utils/calc';
import { buildClientEmail, buildAdminEmail } from '../../utils/emailBuilder';
import PrintPreview from './PrintPreview';
import './Modal.css';

const emptyLine = () => ({ desc: '', qty: 1, unit: 'Unité', prix: 0 });

// ── Chargement SDK FedaPay ────────────────────────────────────
function useFedaPay() {
  const [ready, setReady] = useState(!!window.FedaPay);
  useEffect(() => {
    if (window.FedaPay) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.fedapay.com/checkout.js?v=1.1.7';
    s.async = true;
    s.onload  = () => setReady(true);
    s.onerror = () => console.error('FedaPay SDK non chargé');
    document.body.appendChild(s);
  }, []);
  return ready;
}

export default function FactureModal({ onClose }) {
  const { currentUser, data, saveTransaction, updateTransaction, showToast } = useApp();
  const fedaReady = useFedaPay();

  const [num,     setNum]     = useState(genNum('FAC'));
  const [date,    setDate]    = useState(today());
  const [valid,   setValid]   = useState(30);
  const [cnom,    setCnom]    = useState('');
  const [ctel,    setCtel]    = useState('');
  const [cemail,  setCemail]  = useState('');
  const [cadr,    setCadr]    = useState('');
  const [lines,   setLines]   = useState([emptyLine()]);
  const [tva,     setTva]     = useState(0);
  const [remise,  setRemise]  = useState(0);
  const [acompte, setAcompte] = useState(0);
  const [notes,   setNotes]   = useState(
    'Paiement accepté : Mobile Money, Virement bancaire, Espèces.\nDélai de paiement : 30 jours après réception.'
  );
  const [preview,  setPreview]  = useState(false);
  const [mailInfo, setMailInfo] = useState(null);
  const [savedId,  setSavedId]  = useState(null);
  const [payStatus, setPayStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  const totals = calcDoc(lines, tva, remise, acompte);

  function updateLine(i, key, val) {
    setLines(l => l.map((ln, j) => j === i ? { ...ln, [key]: val } : ln));
  }
  function removeLine(i) { setLines(l => l.filter((_, j) => j !== i)); }

  async function handleSave() {
    const id = await saveTransaction(
      'facture', lines,
      { num, date, valid, cnom, ctel, cemail, cadr, notes },
      totals
    );
    setSavedId(id);
    showToast('Facture enregistrée !');
    return id;
  }

  function handleMail() {
    const worker  = currentUser?.name || '—';
    const dateStr = new Date(date).toLocaleDateString('fr-FR');
    const ctx = {
      type:'facture', num, dateStr, valid, cnom, cemail,
      worker, totals, contact: data.contact, paiement: data.paiement
    };
    const bodyClient = encodeURIComponent(buildClientEmail(ctx));
    const bodyAdmin  = encodeURIComponent(buildAdminEmail(ctx));
    setMailInfo({
      clientHref: cemail
        ? `mailto:${cemail}?subject=${encodeURIComponent('KOTOSTECHNOLOGY – Facture N° ' + num)}&body=${bodyClient}`
        : null,
      adminHref: `mailto:${data.contact?.email}?subject=${encodeURIComponent('[KOTOS] Facture N° ' + num + ' – ' + (cnom||'Client') + ' – par ' + worker)}&body=${bodyAdmin}`,
      cemail,
    });
    handleSave();
  }

  // ── Paiement FedaPay ─────────────────────────────────────
  async function handlePay() {
    if (!fedaReady || !window.FedaPay) {
      showToast('SDK FedaPay non chargé', 'error');
      return;
    }

    // Sauvegarder d'abord si pas encore fait
    let txId = savedId;
    if (!txId) txId = await handleSave();

    setPayStatus('loading');

    window.FedaPay.init({
      public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY ||
                  'pk_live_5ZFG7_VsT85YMgYhpxSx4W04',
      transaction: {
        amount:      Math.round(totals.restant > 0 ? totals.restant : totals.total),
        description: `Facture ${num} – ${cnom || 'Client'} – KOTOSTECHNOLOGY`,
      },
      customer: {
        firstname: cnom || 'Client',
        lastname:  '',
        email:     cemail || '',
        phone_number: {
          number:  ctel?.replace(/\s/g, '') || '',
          country: 'BJ',
        },
      },
      currency:  { iso: 'XOF' },
      onComplete: async function(resp) {
        if (resp.reason === window.FedaPay.DIALOG_DISMISSED) {
          setPayStatus(null);
          showToast('Paiement annulé', 'warn');
          return;
        }
        const tx = resp.transaction;
        if (tx?.status === 'approved') {
          setPayStatus('success');
          // Mettre à jour la facture dans Firebase
          await updateTransaction(txId, {
            fedapay_status: 'approved',
            fedapay_tx_id:  String(tx.id),
            fedapay_date:   new Date().toLocaleDateString('fr-FR'),
            fedapay_mode:   tx.payment_method || 'mobile_money',
            statut:         'payé',
          });
          showToast('✅ Paiement confirmé ! Facture mise à jour.', 'ok');
        } else {
          setPayStatus('error');
          showToast('❌ Paiement échoué ou refusé', 'error');
        }
      },
    }).open();
  }

  // ── Styles internes ──────────────────────────────────────
  const payBtnStyle = {
    padding: '11px 20px', borderRadius: '8px', border: 'none',
    fontWeight: 700, fontSize: '13px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px',
    transition: 'all .2s', whiteSpace: 'nowrap',
  };

  return (
    <div className="overlay open">
      <div className="modal modal-lg">
        <div className="mhead">
          <h2>🧾 Facture — <span className="worker-lbl">{currentUser?.name}</span></h2>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="mbody">

          {/* Metadata */}
          <div className="grid3">
            <div className="fg2"><label>N° Facture</label><input value={num} onChange={e=>setNum(e.target.value)}/></div>
            <div className="fg2"><label>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div className="fg2"><label>Validité (jours)</label><input type="number" value={valid} min={1} onChange={e=>setValid(+e.target.value)}/></div>
          </div>
          <div className="grid3">
            <div className="fg2"><label>Client – Nom *</label><input value={cnom} onChange={e=>setCnom(e.target.value)} placeholder="Nom du client"/></div>
            <div className="fg2"><label>Téléphone</label><input value={ctel} onChange={e=>setCtel(e.target.value)} placeholder="+229 XX XX"/></div>
            <div className="fg2"><label>Email client</label><input type="email" value={cemail} onChange={e=>setCemail(e.target.value)} placeholder="client@email.com"/></div>
          </div>
          <div className="fg2"><label>Adresse client</label><input value={cadr} onChange={e=>setCadr(e.target.value)} placeholder="Adresse"/></div>

          {/* Lines */}
          <h3 className="section-sep">Prestations</h3>
          <div className="lines-header">
            <span>Description</span><span>Qté</span><span>Unité</span><span>Prix unit.</span><span/>
          </div>
          {lines.map((ln, i) => (
            <div key={i} className="fact-line">
              <input className="fact-inp" value={ln.desc} onChange={e=>updateLine(i,'desc',e.target.value)} placeholder="Description"/>
              <input className="fact-inp center" type="number" min={1} value={ln.qty} onChange={e=>updateLine(i,'qty',+e.target.value)}/>
              <input className="fact-inp" value={ln.unit} onChange={e=>updateLine(i,'unit',e.target.value)}/>
              <input className="fact-inp" type="number" min={0} value={ln.prix} onChange={e=>updateLine(i,'prix',+e.target.value)}/>
              <button className="fact-del" onClick={()=>removeLine(i)}>✕</button>
            </div>
          ))}
          <button className="add-line-btn" onClick={()=>setLines(l=>[...l,emptyLine()])}>+ Ligne</button>

          {/* Totals config */}
          <div className="grid3" style={{marginTop:14}}>
            <div className="fg2"><label>TVA %</label><input type="number" value={tva} min={0} max={100} onChange={e=>setTva(+e.target.value)}/></div>
            <div className="fg2"><label>Remise %</label><input type="number" value={remise} min={0} max={100} onChange={e=>setRemise(+e.target.value)}/></div>
            <div className="fg2"><label>Acompte reçu (FCFA)</label><input type="number" value={acompte} min={0} onChange={e=>setAcompte(+e.target.value)}/></div>
          </div>

          {/* Totals display */}
          <div className="totals-box">
            <div className="tot-row"><span>Sous-total :</span><span>{fmt(totals.sub)}</span></div>
            {totals.remisePct > 0 && <div className="tot-row"><span>Remise ({totals.remisePct}%) :</span><span>– {fmt(totals.remiseAmt)}</span></div>}
            {totals.tvaPct > 0 && <div className="tot-row"><span>TVA ({totals.tvaPct}%) :</span><span>{fmt(totals.tvaAmt)}</span></div>}
            <div className="tot-row big"><span>TOTAL :</span><span>{fmt(totals.total)}</span></div>
            {totals.acompte > 0 && <>
              <div className="tot-row"><span>Acompte reçu :</span><span>– {fmt(totals.acompte)}</span></div>
              <div className="tot-row red"><span>Reste à payer :</span><span>{fmt(totals.restant)}</span></div>
            </>}
          </div>

          <div className="fg2" style={{marginTop:12}}>
            <label>Notes / Conditions</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}/>
          </div>

          {/* ── Zone Paiement FedaPay ── */}
          <div style={{
            margin: '16px 0 8px',
            padding: '16px',
            background: 'rgba(0,204,136,0.05)',
            border: '1px solid rgba(0,204,136,0.25)',
            borderRadius: '10px',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
              <span style={{ fontSize:'16px' }}>💳</span>
              <span style={{ fontWeight:700, color:'#00CC88', fontSize:'14px' }}>Paiement en ligne</span>
              <span style={{ fontSize:'11px', color:'#8FA0BC' }}>MTN MoMo · Moov Money · Celtis · Carte bancaire</span>
            </div>

            {/* Statut paiement */}
            {payStatus === 'success' && (
              <div style={{ background:'rgba(0,204,136,0.15)', border:'1px solid #00CC88', borderRadius:'8px', padding:'10px 14px', marginBottom:'12px', color:'#00CC88', fontWeight:700 }}>
                ✅ Paiement confirmé ! La facture est marquée comme payée.
              </div>
            )}
            {payStatus === 'error' && (
              <div style={{ background:'rgba(255,64,96,0.15)', border:'1px solid #FF4060', borderRadius:'8px', padding:'10px 14px', marginBottom:'12px', color:'#FF4060', fontWeight:700 }}>
                ❌ Paiement échoué. Veuillez réessayer.
              </div>
            )}

            <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center' }}>
              {/* Montant à payer */}
              <div style={{ background:'rgba(0,0,0,0.3)', borderRadius:'8px', padding:'8px 14px', fontSize:'13px' }}>
                <span style={{ color:'#8FA0BC' }}>Montant : </span>
                <span style={{ color:'#00CC88', fontWeight:900 }}>
                  {fmt(totals.restant > 0 ? totals.restant : totals.total)} FCFA
                </span>
              </div>

              {/* Bouton payer */}
              {payStatus !== 'success' && (
                <button
                  onClick={handlePay}
                  disabled={payStatus === 'loading' || !cnom}
                  style={{
                    ...payBtnStyle,
                    background: payStatus === 'loading' ? '#555' : 'linear-gradient(135deg,#00aa66,#00CC88)',
                    color: '#fff',
                    opacity: (!cnom || payStatus === 'loading') ? 0.6 : 1,
                    cursor: (!cnom || payStatus === 'loading') ? 'not-allowed' : 'pointer',
                  }}
                >
                  {payStatus === 'loading' ? '⏳ En cours...' : '💳 Payer maintenant'}
                </button>
              )}

              {/* Bouton réessayer */}
              {payStatus === 'error' && (
                <button onClick={() => setPayStatus(null)} style={{ ...payBtnStyle, background:'rgba(255,64,96,0.2)', color:'#FF4060', border:'1px solid rgba(255,64,96,0.4)' }}>
                  🔄 Réessayer
                </button>
              )}

              {!cnom && (
                <span style={{ fontSize:'11px', color:'#D4A017' }}>⚠ Saisissez le nom du client pour activer le paiement</span>
              )}
            </div>

            {/* Logos modes de paiement */}
            <div style={{ display:'flex', gap:'6px', marginTop:'10px', flexWrap:'wrap' }}>
              {['📱 MTN MoMo', '📱 Moov Money', '📱 Celtis Cash', '💳 Carte bancaire'].map(m => (
                <span key={m} style={{ fontSize:'11px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'3px 8px', color:'#8FA0BC' }}>
                  {m}
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="action-btns">
            <button className="abtn abtn-blue"  onClick={()=>setPreview(p=>!p)}>{preview ? "▲ Masquer l'aperçu" : "👁 Aperçu / Imprimer"}</button>
            <button className="abtn abtn-green" onClick={handleMail}>📧 Préparer email</button>
            <button className="abtn abtn-muted" onClick={handleSave}>💾 Enregistrer</button>
          </div>

          {/* Mail info */}
          {mailInfo && (
            <div className="mail-ready">
              <p className="mail-title">✓ Email prêt — aucune pièce jointe, expéditeur : <strong>{data.contact?.email}</strong></p>
              <div className="mail-btns">
                {mailInfo.clientHref
                  ? <a href={mailInfo.clientHref} className="abtn abtn-green" style={{textDecoration:'none'}}>📧 Envoyer au client ({mailInfo.cemail})</a>
                  : <span className="mail-warn">⚠ Email client non fourni</span>}
                <a href={mailInfo.adminHref} className="abtn abtn-blue" style={{textDecoration:'none'}}>📨 Notifier l'admin</a>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <PrintPreview type="facture"
              meta={{ num, date, valid, cnom, ctel, cemail, cadr, notes, worker: currentUser?.name }}
              lines={lines} totals={totals}
            />
          )}
        </div>
      </div>
    </div>
  );
}